import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Upload, Eye, Download, Loader2, FileText, Trash2, Link2, Plus, X, ExternalLink } from 'lucide-react';
import { api, apiDownload } from '@/lib/api';
import { toast } from 'sonner';
import API_URL from '../../lib/apiConfig';

const API = API_URL;
const INLINE_VIEWABLE = ['.pdf', '.png', '.jpg', '.jpeg', '.gif'];

// A link attachment carries kind="link" and a url instead of a stored file.
const isLinkAttachment = (att) => att?.kind === 'link' || Boolean(att?.url);

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Attachments inside the SOW workspace. These are the SAME records as the
 * Opportunities module — one shared store keyed by opportunity_id — so a file
 * added here shows up there and vice-versa. View + upload + delete (Ryan,
 * 18 Jun); deleting here also removes the file from the Opportunities module,
 * which is intended — the user is warned before it happens.
 */
export default function SalesOpsAttachmentsPanel({ opportunityId }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!opportunityId) return;
    setLoading(true);
    try {
      const res = await api.get(
        `${API}/api/sales-ops/opportunities/${opportunityId}/attachments`,
      );
      if (res.ok) {
        const data = await res.json();
        setAttachments(data.attachments || []);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load attachments');
      }
    } catch (e) {
      console.error('Error loading attachments:', e);
      toast.error('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    let uploaded = 0;
    try {
      for (const file of files) {
        // Matches backend MAX_FILE_SIZE (50MB) in services/attachment_store.py
        if (file.size > 50 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 50MB — skipped`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.postForm(
          `${API}/api/sales-ops/opportunities/${opportunityId}/attachments`,
          formData,
        );
        if (res.ok) {
          uploaded += 1;
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || `Failed to upload ${file.name}`);
        }
      }
      if (uploaded > 0) {
        toast.success(uploaded > 1 ? `${uploaded} files uploaded` : 'File uploaded');
        load();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleDelete = async (att) => {
    const name = att.filename || 'this attachment';
    // Deleting removes the file from the shared store, so it also disappears from
    // the Opportunities module — make that explicit before destroying anything.
    const ok = window.confirm(
      `Delete "${name}"?\n\nThis also removes it from the Opportunity's attachments and can't be undone.`,
    );
    if (!ok) return;

    setDeletingId(att.attachment_id);
    try {
      const res = await api.delete(
        `${API}/api/sales-ops/opportunities/${opportunityId}/attachments/${att.attachment_id}`,
      );
      if (res.ok) {
        toast.success('Attachment deleted');
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete attachment');
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddLink = async () => {
    const url = linkUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Enter a valid link starting with http:// or https://');
      return;
    }
    setAddingLink(true);
    try {
      const res = await api.post(
        `${API}/api/sales-ops/opportunities/${opportunityId}/attachments/link`,
        { url, title: linkTitle.trim() || undefined },
      );
      if (res.ok) {
        toast.success('Link added');
        setShowLinkDialog(false);
        setLinkUrl('');
        setLinkTitle('');
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add link');
      }
    } catch (error) {
      console.error('Error adding link:', error);
      toast.error('Failed to add link');
    } finally {
      setAddingLink(false);
    }
  };

  const handleOpen = async (att) => {
    // A link attachment isn't stored as a file — just open its URL in a new tab.
    if (isLinkAttachment(att)) {
      window.open(att.url, '_blank', 'noopener,noreferrer');
      return;
    }
    const name = att.filename || 'attachment';
    const viewInline = INLINE_VIEWABLE.some((ext) => name.toLowerCase().endsWith(ext));
    // Claim the tab synchronously inside the click handler so the popup blocker
    // treats it as user-initiated.
    const win = viewInline ? window.open('', '_blank') : null;
    try {
      const blob = await apiDownload(
        `${API}/api/sales-ops/opportunities/${opportunityId}/attachments/${att.attachment_id}/download`,
      );
      if (!blob) {
        win?.close();
        toast.error('Failed to open attachment');
        return;
      }
      const url = URL.createObjectURL(blob);
      if (!viewInline) {
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 0);
        return;
      }
      if (!win) {
        URL.revokeObjectURL(url);
        toast.error('Allow pop-ups to view attachments');
        return;
      }
      win.location = url;
      const startedAt = Date.now();
      const poll = setInterval(() => {
        if (win.closed || Date.now() - startedAt > 30 * 60 * 1000) {
          clearInterval(poll);
          URL.revokeObjectURL(url);
        }
      }, 1000);
    } catch (error) {
      win?.close();
      console.error('Error opening attachment:', error);
      toast.error('Failed to open attachment');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-boss-text flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            Attachments
          </h3>
          <p className="text-sm text-boss-text-secondary mt-1">
            Shared with the Opportunity — files added here are visible in the
            Opportunities module, and vice-versa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleUpload}
            data-testid="sales-ops-attachment-input"
          />
          <button
            type="button"
            onClick={() => setShowLinkDialog(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-boss-border text-boss-text text-sm font-medium hover:bg-boss-surface transition-colors"
            data-testid="sales-ops-attachment-add-link"
          >
            <Link2 className="w-4 h-4" />
            Add link
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
            data-testid="sales-ops-attachment-upload"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {uploading ? 'Uploading…' : 'Upload file'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-boss-text-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading attachments…
        </div>
      ) : attachments.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-boss-border rounded-lg">
          <FileText className="w-10 h-10 text-boss-text-muted mx-auto mb-2" />
          <p className="text-boss-text-secondary text-sm">No attachments yet.</p>
          <p className="text-boss-text-muted text-xs mt-1">
            Upload a file or add a link — they share the same store as the Opportunity.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-boss-border border border-boss-border rounded-lg overflow-hidden">
          {attachments.map((att) => (
            <div
              key={att.attachment_id}
              className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-boss-elevated transition-colors"
              data-testid={`sales-ops-attachment-${att.attachment_id}`}
            >
              <div className="min-w-0 flex items-center gap-3">
                {isLinkAttachment(att) ? (
                  <Link2 className="w-5 h-5 text-violet-500 shrink-0" />
                ) : (
                  <FileText className="w-5 h-5 text-boss-text-muted shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-boss-text text-sm font-medium truncate">
                    {att.filename || (isLinkAttachment(att) ? att.url : 'attachment')}
                  </p>
                  <p className="text-boss-text-muted text-xs truncate">
                    {isLinkAttachment(att) ? att.url : formatSize(att.file_size)}
                    {att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => handleOpen(att)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-boss-text-muted hover:text-violet-600 dark:hover:text-violet-400 hover:bg-boss-surface transition-colors"
                  title={isLinkAttachment(att) ? 'Open link' : 'View or download'}
                  data-testid={`sales-ops-attachment-open-${att.attachment_id}`}
                >
                  {isLinkAttachment(att) ? (
                    <ExternalLink className="w-4 h-4" />
                  ) : INLINE_VIEWABLE.some((ext) =>
                    (att.filename || '').toLowerCase().endsWith(ext),
                  ) ? (
                    <Eye className="w-4 h-4" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(att)}
                  disabled={deletingId === att.attachment_id}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-boss-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-boss-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete attachment"
                  data-testid={`sales-ops-attachment-delete-${att.attachment_id}`}
                >
                  {deletingId === att.attachment_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showLinkDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !addingLink && setShowLinkDialog(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-boss-elevated border border-boss-border shadow-xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-boss-text font-semibold flex items-center gap-2">
                <Link2 className="w-4 h-4 text-violet-500" />
                Add a link
              </h3>
              <button
                type="button"
                onClick={() => !addingLink && setShowLinkDialog(false)}
                className="text-boss-text-muted hover:text-boss-text p-1 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-boss-text-muted text-xs mb-3">
              Paste a link to a Google Sheet, Drive file, or any web page. It's saved
              alongside your files and shared with the Opportunity.
            </p>
            <label className="block text-xs font-medium text-boss-text-secondary mb-1">Link (URL)</label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/…"
              autoFocus
              className="w-full mb-3 px-3 py-2 rounded-md border border-boss-border bg-boss-surface text-boss-text text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="sales-ops-link-url"
            />
            <label className="block text-xs font-medium text-boss-text-secondary mb-1">
              Title <span className="text-boss-text-muted">(optional)</span>
            </label>
            <input
              type="text"
              value={linkTitle}
              onChange={(e) => setLinkTitle(e.target.value)}
              placeholder="e.g. Q3 Pricing Sheet"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddLink();
              }}
              className="w-full mb-4 px-3 py-2 rounded-md border border-boss-border bg-boss-surface text-boss-text text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="sales-ops-link-title"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowLinkDialog(false)}
                disabled={addingLink}
                className="px-3 py-2 rounded-md border border-boss-border text-boss-text text-sm hover:bg-boss-surface transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddLink}
                disabled={addingLink || !linkUrl.trim()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="sales-ops-link-submit"
              >
                {addingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {addingLink ? 'Adding…' : 'Add link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
